import React, { useRef, useState, useEffect } from 'react';
import { FaStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { BsCheckCircle, BsFillPersonFill } from 'react-icons/bs';
import { motion, AnimatePresence } from 'framer-motion';
import felix from '../../Assets/feedback/Screenshot_2025-08-13_at_2.05.48_AM_gaxkms.png';
import ebony from '../../Assets/feedback/732ac001-2a27-4e62-a887-f08f200ef8ab.png';

const testimonials = [
  {
    name: 'Felix Weber',
    image: felix,
    role: 'Freelancer & Consultant',
    review:
      'Very reliable and prompt delivery. The interface is easy to use and the support team resolved my issue within hours.',
    summary: 'good',
    rating: 5,
    verified: true,
  },
  {
    name: 'Bobi Gill-Brooks',
    image: ebony,
    role: 'Ebony JP',
    review:
      'Words rarely fail me, but I am having difficulty coming up with the appropriate words to express my gratitude to the team at www.thosewebdudes.com. I appreciate and thank them for their patience with ME, with my insane SCHEDULE, and with my LACK of computer savvy ! They created my site to be something beyond what I had envisioned and I so appreciate their professionalism and timeliness! I sing their praises and HIGHLY recommend their services and work to anyone in search of a web designing team! If you have a vision for what you would like to see, they will take it and make it MORE! Ever grateful and satisfied customer!',
    summary: 'good',
    rating: 5,
    verified: true,
  },
  {
    name: 'Sudipta Karmakar',
    role: 'Owner at Paradise Car Rental',
    review:
      'The attention to detail and premium quality exceeded my expectations. The customer service was exceptional, and the product arrived earlier than expected.',
    summary: 'Outstanding Experience',
    rating: 5,
    verified: true,
  },
  {
    name: 'Mofijul Mondal',
    role: 'Service Manager at NestFixers',
    review:
      'good service! The team was responsive and professional. I was amazed by how smooth the entire process was.',
    summary: 'Outstanding Experience',
    rating: 4,
    verified: true,
  },
  {
    name: 'Aurodeep Paul',
    role: 'Founder of Avishree Hospitality Pvt. Ltd.',
    review:
      'Very reliable and prompt delivery. The interface is easy to use and the support team resolved my issue within hours.',
    summary: 'Outstanding Experience',
    rating: 5,
    verified: true,
  },
  {
    name: 'Suman Dutta',
    role: 'Business Owner',
    review:
      'These guys are amazing! They delivered exactly what I needed, and the quality was top-notch. I will definitely be back for more.',
    summary: 'Outstanding Experience',
    rating: 5,
    verified: true,
  },
  {
    name: 'Akash Chowdhury',
    role: 'Freelancer & Graphic Designer',
    review:
      'I was impressed with the level of professionalism and expertise. The product was delivered on time and exceeded my expectations.',
    summary: 'Outstanding Experience',
    rating: 5,
    verified: false,
  }
];

const Card = () => {
  const scrollContainerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState(0); // -1 for left, 1 for right, 0 for initial

  // Auto-slide effect with infinite loop
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isAnimating) {
        handleNext();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isAnimating, currentIndex, testimonials.length]); // Added testimonials.length to dependencies

  const handlePrevious = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setDirection(-1);
    
    // If at the beginning of the visible set, jump to the end of the previous set
    if (currentIndex === 0) {
      setCurrentIndex(testimonials.length - 3);
    } else {
      setCurrentIndex(prev => prev - 1);
    }
    
    setTimeout(() => setIsAnimating(false), 600);
  };

  const handleNext = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setDirection(1);
    
    // If at the end of the visible set, loop back to beginning
    if (currentIndex >= testimonials.length - 3) {
      setCurrentIndex(0);
    } else {
      setCurrentIndex(prev => prev + 1);
    }
    
    setTimeout(() => setIsAnimating(false), 600);
  };

  // Calculate visible cards with loop logic
  const getVisibleCards = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const actualIndex = (currentIndex + i) % testimonials.length;
      visible.push({
        ...testimonials[actualIndex],
        originalIndex: actualIndex,
        displayIndex: currentIndex + i
      });
    }
    return visible;
  };

  // Card variants for animation with directional awareness
  const cardVariants = {
    enter: (direction) => ({
      opacity: 0,
      scale: 0.8,
      x: direction > 0 ? 100 : -100,
      filter: "blur(10px)",
      rotateY: direction > 0 ? 15 : -15,
    }),
    center: {
      opacity: 1,
      scale: 1,
      x: 0,
      filter: "blur(0px)",
      rotateY: 0,
    },
    exit: (direction) => ({
      opacity: 0,
      scale: 0.8,
      x: direction > 0 ? -100 : 100,
      filter: "blur(10px)",
      rotateY: direction > 0 ? -15 : 15,
    })
  };

  const buttonVariants = {
    initial: { scale: 1, opacity: 0.8 },
    hover: { 
      scale: 1.1, 
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 400, 
        damping: 10 
      }
    },
    tap: { scale: 0.95 }
  };

  const visibleCards = getVisibleCards();

  return (
    <div className="w-full overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true, margin: "-50px" }}
        className="text-center mb-16"
      >
        <motion.h2 
          className="text-4xl font-bold mb-4 text-gray-200"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Client <span className="text-blue-600 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Testimonials</span>
        </motion.h2>
        <motion.p 
          className="text-xl text-gray-200 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          What our clients say about working with us
        </motion.p>
      </motion.div>

      {/* Desktop View with Navigation Buttons */}
      <div className="hidden lg:block relative py-8">
        {/* Navigation Buttons - Always visible for infinite loop */}
        <motion.button
          variants={buttonVariants}
          initial="initial"
          whileHover="hover"
          whileTap="tap"
          onClick={handlePrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-slate-900/90 hover:bg-slate-800/95 border border-slate-700/50 rounded-2xl p-4 shadow-2xl backdrop-blur-xl transition-all duration-200"
          aria-label="Previous testimonials"
        >
          <FaChevronLeft className="h-6 w-6 text-white drop-shadow-lg" />
        </motion.button>

        <motion.button
          variants={buttonVariants}
          initial="initial"
          whileHover="hover"
          whileTap="tap"
          onClick={handleNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-slate-900/90 hover:bg-slate-800/95 border border-slate-700/50 rounded-2xl p-4 shadow-2xl backdrop-blur-xl transition-all duration-200"
          aria-label="Next testimonials"
        >
          <FaChevronRight className="h-6 w-6 text-white drop-shadow-lg" />
        </motion.button>

        {/* Cards Container for Desktop */}
        <div className="flex justify-center gap-8 px-12">
          <AnimatePresence mode="popLayout" custom={direction}>
            {visibleCards.map((item, index) => (
              <motion.div
                key={`${item.displayIndex}-${item.originalIndex}`}
                custom={direction}
                variants={cardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.4 },
                  scale: { duration: 0.4 },
                  filter: { duration: 0.4 }
                }}
                className="group relative w-[350px] flex-shrink-0"
                whileHover={{ 
                  y: -8,
                  transition: { type: "spring", stiffness: 300, damping: 20 }
                }}
              >
                <motion.div 
                  className="relative h-full overflow-hidden rounded-3xl bg-slate-950/80 shadow-2xl border border-slate-800/50 backdrop-blur-sm"
                  whileHover={{
                    boxShadow: "0 25px 50px -12px rgba(99, 102, 241, 0.25)"
                  }}
                >
                  {/* Animated Glowing background circles */}
                  <motion.div 
                    className="absolute -left-16 -top-16 h-32 w-32 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-500/0 blur-2xl"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                  <motion.div 
                    className="absolute -right-16 -bottom-16 h-32 w-32 rounded-full bg-gradient-to-br from-purple-500/30 to-indigo-500/0 blur-2xl"
                    animate={{
                      scale: [1.2, 1, 1.2],
                      opacity: [0.6, 0.3, 0.6],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 2
                    }}
                  />

                  {/* Card Content */}
                  <div className="relative p-6 h-full flex flex-col z-10">
                    {/* User Info */}
                    <motion.div 
                      className="flex items-center gap-4 mb-6"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                    >
                      <div className="group/avatar relative">
                        <motion.div 
                          className="absolute -inset-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 opacity-75 blur"
                          animate={{
                            rotate: [0, 360],
                          }}
                          transition={{
                            duration: 8,
                            repeat: Infinity,
                            ease: "linear"
                          }}
                        />
                        <div className="relative h-12 w-12 rounded-full bg-slate-950 ring-2 ring-slate-800 flex items-center justify-center overflow-hidden">
                          {item.image ? (
                            <motion.img
                              src={item.image}
                              alt={item.name}
                              className="h-full w-full object-cover"
                              whileHover={{ scale: 1.1 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            />
                          ) : (
                            <BsFillPersonFill className="h-6 w-6 text-indigo-500" />
                          )}
                        </div>
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-white truncate">{item.name}</h4>
                        <p className="text-sm text-slate-400 truncate">{item.role}</p>
                      </div>
                      {item.verified && (
                        <motion.div 
                          className="ml-auto flex-shrink-0"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5 + index * 0.1 }}
                        >
                          <div className="flex items-center gap-1 rounded-full bg-indigo-500/10 px-3 py-1 border border-indigo-500/20">
                            <BsCheckCircle className="h-4 w-4 text-indigo-500" />
                            <span className="text-xs font-medium text-indigo-500">Verified</span>
                          </div>
                        </motion.div>
                      )}
                    </motion.div>

                    {/* Rating */}
                    <motion.div 
                      className="flex items-center gap-1 mb-4"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                    >
                      {[...Array(item.rating)].map((_, i) => (
                        <motion.div
                          key={i}
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{ 
                            delay: 0.6 + (i * 0.1) + (index * 0.05),
                            type: "spring", 
                            stiffness: 300 
                          }}
                        >
                          <FaStar className="h-5 w-5 text-amber-400 drop-shadow" />
                        </motion.div>
                      ))}
                    </motion.div>

                    {/* Review Content */}
                    <motion.div 
                      className="space-y-2 flex-grow"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                    >
                      <h3 className="text-xl font-semibold text-white bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                        {item.summary}
                      </h3>
                      <p className="text-slate-400 line-clamp-3 leading-relaxed">
                        {item.review}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Mobile View with Horizontal Scroll */}
     <div className="lg:hidden relative py-2">
  <motion.div
    ref={scrollContainerRef}
    className="flex overflow-x-auto scrollbar-hide gap-6 px-4 snap-x snap-mandatory"
    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }} 
    transition={{ duration: 0.6 }}
    viewport={{ once: true }}
  >
    {/* Add padding items to center the first card */}
    <div className="flex-shrink-0 w-[calc(50vw-160px)]" />
    
    {testimonials.map((item, index) => (
      <motion.div
        key={index}
        className="group relative w-[320px] flex-shrink-0 snap-center" 
        initial={{ opacity: 0, x: 50 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        viewport={{ once: true, margin: "-50px" }}
        whileHover={{ y: -5 }}
      >
        <div className="relative h-full overflow-hidden rounded-2xl bg-slate-950/80 shadow-2xl border border-slate-800/50 backdrop-blur-sm">
          {/* Mobile Glow Effects */}
          <motion.div 
            className="absolute -left-16 -top-16 h-32 w-32 rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/0 blur-2xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          {/* Card Content */}
          <div className="relative p-6 h-full flex flex-col z-10">
            {/* User Info */}
            <div className="flex items-center gap-4 mb-6">
              <div className="group/avatar relative">
                <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 opacity-50 blur transition-all duration-300 group-hover/avatar:opacity-75" />
                <div className="relative h-12 w-12 rounded-full bg-slate-950 ring-2 ring-slate-800 flex items-center justify-center overflow-hidden">
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <BsFillPersonFill className="h-6 w-6 text-indigo-500" />
                  )}
                </div>
              </div>
              <div className="min-w-0">
                <h4 className="font-semibold text-white truncate">{item.name}</h4>
                <p className="text-sm text-slate-400 truncate">{item.role}</p>
              </div>
              {item.verified && (
                <div className="ml-auto flex-shrink-0">
                  <div className="flex items-center gap-1 rounded-full bg-indigo-500/10 px-3 py-1 border border-indigo-500/20">
                    <BsCheckCircle className="h-4 w-4 text-indigo-500" />
                    <span className="text-xs font-medium text-indigo-500">Verified</span>
                  </div>
                </div>
              )}
            </div>

            {/* Rating */}
            <div className="flex items-center gap-1 mb-4">
              {[...Array(item.rating)].map((_, i) => (
                <FaStar key={i} className="h-5 w-5 text-amber-400" />
              ))}
            </div>

            {/* Review Content */}
            <div className="space-y-2 flex-grow">
              <h3 className="text-xl font-semibold text-white">{item.summary}</h3>
              <p className="text-slate-400 line-clamp-3">{item.review}</p>
            </div>
          </div>
        </div>
      </motion.div>
    ))}
    
    {/* Add padding items to center the last card */}
    <div className="flex-shrink-0 w-[calc(50vw-160px)]" />
  </motion.div>
</div>

      {/* Dots Indicator for Desktop */}
      <motion.div 
        className="hidden lg:flex justify-center gap-2 mt-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        {Array.from({ length: testimonials.length }).map((_, index) => (
          <motion.button
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex % testimonials.length ? 'bg-indigo-500 w-6' : 'bg-slate-700'
            }`}
            whileHover={{ scale: 1.2 }}
            onClick={() => {
              setIsAnimating(true);
              setCurrentIndex(index);
              setTimeout(() => setIsAnimating(false), 500);
            }}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </motion.div>

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default Card;